import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  PencilIcon, TrashIcon, PhoneIcon, EnvelopeIcon,
  BuildingOfficeIcon, MapPinIcon, PlusIcon, TagIcon,
  CurrencyDollarIcon, CalendarIcon, ClockIcon,
  UserIcon, ChartBarIcon, DocumentTextIcon,
  CheckCircleIcon, GlobeAltIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { PageHeader } from '../../components/layout';
import {
  Card, CardBody, CardHeader, CardTitle, Button, Avatar, StatusBadge, Badge,
  Tabs, TabList, Tab, TabPanels, TabPanel, PageLoader, EmptyState,
  Modal, ModalBody, ModalFooter, Input, Select, Textarea, StarRating
} from '../../components/ui';
import { clientService } from '../../services';
import { useUIStore } from '../../stores';
import { useForm, Controller } from 'react-hook-form';

export default function ClientDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { confirm } = useUIStore();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [editingInteraction, setEditingInteraction] = useState(null);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [accountCreated, setAccountCreated] = useState(null);

  const { register: registerInteraction, handleSubmit: handleInteractionSubmit, reset: resetInteraction } = useForm();
  const { register: registerFollowUp, handleSubmit: handleFollowUpSubmit, reset: resetFollowUp, control: controlFollowUp } = useForm();

  useEffect(() => {
    fetchClient();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchClient = async () => {
    try {
      const data = await clientService.getClient(id);
      setClient(data);
    } catch (error) {
      toast.error(t('errors.notFound'));
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: t('common.delete'),
      message: t('clients.confirmDelete') || 'Are you sure you want to delete this client?',
      confirmText: t('common.delete'),
      variant: 'danger'
    });

    if (confirmed) {
      try {
        await clientService.deleteClient(id);
        toast.success(t('success.deleted'));
        navigate('/clients');
      } catch (error) {
        toast.error(error.response?.data?.message || t('errors.serverError'));
      }
    }
  };

  const onInteractionSubmit = async (data) => {
    try {
      if (editingInteraction) {
        await clientService.updateInteraction(id, editingInteraction._id, data);
        toast.success(t('success.updated'));
      } else {
        await clientService.addInteraction(id, data);
        toast.success(t('success.created'));
      }
      setShowInteractionModal(false);
      setEditingInteraction(null);
      resetInteraction();
      fetchClient();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    }
  };

  const handleEditInteraction = (interaction) => {
    setEditingInteraction(interaction);
    resetInteraction({
      type: interaction.type,
      subject: interaction.subject || '',
      content: interaction.content,
      date: interaction.date ? dayjs(interaction.date).format('YYYY-MM-DD') : '',
      duration: interaction.duration || 0
    });
    setShowInteractionModal(true);
  };

  const handleDeleteInteraction = async (interactionId) => {
    const confirmed = await confirm({
      title: t('common.delete'),
      message: t('clients.confirmDeleteInteraction') || 'Are you sure you want to delete this interaction?',
      confirmText: t('common.delete'),
      variant: 'danger'
    });

    if (confirmed) {
      try {
        await clientService.deleteInteraction(id, interactionId);
        toast.success(t('success.deleted'));
        fetchClient();
      } catch (error) {
        toast.error(error.response?.data?.message || t('errors.serverError'));
      }
    }
  };

  const handleCreateAccount = async () => {
    try {
      const result = await clientService.createClientAccount(id);
      setAccountCreated(result);
      toast.success(t('clients.accountCreated') || 'Client account created successfully');
      fetchClient();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    }
  };

  const onFollowUpSubmit = async (data) => {
    try {
      await clientService.addFollowUp(id, data);
      toast.success(t('success.created'));
      setShowFollowUpModal(false);
      resetFollowUp();
      fetchClient();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    }
  };

  const handleCompleteFollowUp = async (followUpId) => {
    try {
      await clientService.completeFollowUp(id, followUpId);
      toast.success(t('success.updated'));
      fetchClient();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    }
  };

  const interactionTypes = [
    { value: 'call', label: t('clients.interactionTypes.call') },
    { value: 'email', label: t('clients.interactionTypes.email') },
    { value: 'meeting', label: t('clients.interactionTypes.meeting') },
    { value: 'message', label: t('clients.interactionTypes.message') },
    { value: 'note', label: t('clients.interactionTypes.note') }
  ];

  if (loading) {
    return <PageLoader />;
  }

  if (!client) {
    return null;
  }

  const stats = [
    {
      label: t('clients.projects') || 'Projects',
      value: client.projectCount || 0,
      icon: DocumentTextIcon,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50'
    },
    {
      label: t('clients.totalRevenue') || 'Total Revenue',
      value: `${client.currency || 'EGP'} ${(client.totalRevenue || 0).toLocaleString()}`,
      icon: CurrencyDollarIcon,
      color: 'text-green-500',
      bgColor: 'bg-green-50'
    },
    {
      label: t('clients.interactions') || 'Interactions',
      value: client.interactionCount || 0,
      icon: PhoneIcon,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50'
    },
    {
      label: t('clients.pendingFollowUps') || 'Pending Follow-ups',
      value: client.pendingFollowUps || 0,
      icon: ClockIcon,
      color: 'text-orange-500',
      bgColor: 'bg-orange-50'
    }
  ];

  const formatAddress = () => {
    if (!client.address) return null;
    const parts = [
      client.address.street,
      client.address.city,
      client.address.state,
      client.address.country,
      client.address.postalCode
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  return (
    <>
      <Helmet>
        <title>{client.name} - ProCTM</title>
      </Helmet>

      <PageHeader
        title={client.name}
        backUrl="/clients"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" icon={PencilIcon} onClick={() => navigate(`/clients/${id}/edit`)}>
              {t('common.edit')}
            </Button>
            <Button variant="danger" icon={TrashIcon} onClick={handleDelete}>
              {t('common.delete')}
            </Button>
          </div>
        }
      />

      {/* Hero Section */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-shrink-0">
              {client.photo ? (
                <img
                  src={client.photo}
                  alt={client.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                />
              ) : (
                <Avatar name={client.name} size="xl" className="border-4 border-white shadow-lg" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-neutral-dark truncate">{client.name}</h1>
                <StatusBadge status={client.status} />
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                {client.company && (
                  <div className="flex items-center gap-2">
                    <BuildingOfficeIcon className="w-4 h-4" />
                    <span>{client.company}</span>
                  </div>
                )}
                {client.industry && (
                  <div className="flex items-center gap-2">
                    <ChartBarIcon className="w-4 h-4" />
                    <span>{client.industry}</span>
                  </div>
                )}
                {client.source && (
                  <div className="flex items-center gap-2">
                    <GlobeAltIcon className="w-4 h-4" />
                    <span>{client.source}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-6">
                <div>
                  <span className="text-xs text-gray-500">{t('clients.score') || 'Score'}</span>
                  <div className="flex items-center gap-2 mt-1">
                    <StarRating value={client.score || 0} max={5} readonly size="md" />
                    <span className="text-sm font-semibold">{client.score || 0}/5</span>
                  </div>
                </div>
                {client.createdAt && (
                  <div>
                    <span className="text-xs text-gray-500">{t('common.createdAt') || 'Created'}</span>
                    <p className="text-sm font-medium mt-1">
                      {dayjs(client.createdAt).format('MMM D, YYYY')}
                    </p>
                  </div>
                )}
                {client.userAccount && (
                  <div>
                    <Badge variant="success">{t('clients.hasAccount') || 'Has Account'}</Badge>
                  </div>
                )}
              </div>
              {!client.userAccount && client.email && (
                <div className="mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowAccountModal(true)}
                  >
                    {t('clients.createAccount') || 'Create Client Account'}
                  </Button>
                </div>
              )}
              {client.userAccount && (
                <div className="mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const result = await clientService.syncClientProjects(id);
                        toast.success(t('clients.projectsSynced') || `Synced ${result.updated} projects`);
                        fetchClient();
                      } catch (error) {
                        toast.error(error.response?.data?.message || t('errors.serverError'));
                      }
                    }}
                  >
                    {t('clients.syncProjects') || 'Sync Projects'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((stat, index) => (
          <Card key={index}>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{stat.label}</p>
                  <p className="text-xl font-bold text-neutral-dark">{stat.value}</p>
                </div>
                <div className={`${stat.bgColor} p-3 rounded-lg`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Main Content Tabs */}
      <Card>
        <CardBody>
          <Tabs>
            <TabList className="mb-6">
              <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
                {t('common.overview') || 'Overview'}
              </Tab>
              <Tab active={activeTab === 'projects'} onClick={() => setActiveTab('projects')}>
                {t('clients.projects')} ({client.projects?.length || 0})
              </Tab>
              <Tab active={activeTab === 'interactions'} onClick={() => setActiveTab('interactions')}>
                {t('clients.interactions')} ({client.interactions?.length || 0})
              </Tab>
              <Tab active={activeTab === 'followups'} onClick={() => setActiveTab('followups')}>
                {t('clients.followUps')} ({client.followUps?.filter(f => !f.isCompleted).length || 0})
              </Tab>
            </TabList>

            <TabPanels>
              {/* Overview Tab */}
              <TabPanel active={activeTab === 'overview'}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Contact Information */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <UserIcon className="w-5 h-5 text-primary" />
                        {t('clients.contactInformation') || 'Contact Information'}
                      </CardTitle>
                    </CardHeader>
                    <CardBody>
                      <div className="space-y-4">
                        {client.email && (
                          <div className="flex items-start gap-3">
                            <EnvelopeIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs text-gray-500 mb-1">{t('common.email')}</p>
                              <a href={`mailto:${client.email}`} className="text-primary hover:underline font-medium">
                                {client.email}
                              </a>
                            </div>
                          </div>
                        )}
                        {client.phone && (
                          <div className="flex items-start gap-3">
                            <PhoneIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs text-gray-500 mb-1">{t('common.phone')}</p>
                              <a href={`tel:${client.phone}`} className="text-primary hover:underline font-medium">
                                {client.phone}
                              </a>
                            </div>
                          </div>
                        )}
                        {client.company && (
                          <div className="flex items-start gap-3">
                            <BuildingOfficeIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs text-gray-500 mb-1">{t('clients.company')}</p>
                              <p className="font-medium">{client.company}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>

                  {/* Business Information */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <ChartBarIcon className="w-5 h-5 text-primary" />
                        {t('clients.businessInformation') || 'Business Information'}
                      </CardTitle>
                    </CardHeader>
                    <CardBody>
                      <div className="space-y-4">
                        {client.status && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500">{t('common.status')}</span>
                            <StatusBadge status={client.status} />
                          </div>
                        )}
                        {client.source && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500">{t('clients.source')}</span>
                            <Badge variant="secondary">{client.source}</Badge>
                          </div>
                        )}
                        {client.sourceDetails && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">{t('clients.sourceDetails')}</p>
                            <p className="text-sm font-medium">{client.sourceDetails}</p>
                          </div>
                        )}
                        {client.industry && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500">{t('clients.industry')}</span>
                            <span className="text-sm font-medium">{client.industry}</span>
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>

                  {/* Address */}
                  {formatAddress() && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <MapPinIcon className="w-5 h-5 text-primary" />
                          {t('common.address') || 'Address'}
                        </CardTitle>
                      </CardHeader>
                      <CardBody>
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg border border-gray-200">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 p-2 bg-white rounded-lg shadow-sm">
                              <MapPinIcon className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                              {client.address.street && (
                                <p className="text-sm font-semibold text-gray-900">{client.address.street}</p>
                              )}
                              <div className="flex flex-wrap gap-x-2 gap-y-1 text-sm text-gray-600">
                                {client.address.city && (
                                  <span className="after:content-['•'] after:mx-2 last:after:hidden">{client.address.city}</span>
                                )}
                                {client.address.state && (
                                  <span className="after:content-['•'] after:mx-2 last:after:hidden">{client.address.state}</span>
                                )}
                                {client.address.country && (
                                  <span className="after:content-['•'] after:mx-2 last:after:hidden">{client.address.country}</span>
                                )}
                                {client.address.postalCode && (
                                  <span>{client.address.postalCode}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {/* Financial Information */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CurrencyDollarIcon className="w-5 h-5 text-primary" />
                        {t('clients.financialInformation') || 'Financial Information'}
                      </CardTitle>
                    </CardHeader>
                    <CardBody>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">{t('clients.currency') || 'Currency'}</span>
                          <span className="text-sm font-medium">{client.currency || 'EGP'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">{t('dashboard.totalRevenue') || 'Total Revenue'}</span>
                          <span className="text-lg font-bold text-green-600">
                            {client.currency || 'EGP'} {(client.totalRevenue || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  {/* Tags */}
                  {client.tags && client.tags.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <TagIcon className="w-5 h-5 text-primary" />
                          {t('clients.tags') || 'Tags'}
                        </CardTitle>
                      </CardHeader>
                      <CardBody>
                        <div className="flex flex-wrap gap-2">
                          {client.tags.map((tag, index) => (
                            <Badge key={index} variant="neutral">{tag}</Badge>
                          ))}
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {/* Custom Fields */}
                  {client.customFields && client.customFields.length > 0 && (
                    <Card className="lg:col-span-2">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <DocumentTextIcon className="w-5 h-5 text-primary" />
                          {t('clients.customFields') || 'Custom Fields'}
                        </CardTitle>
                      </CardHeader>
                      <CardBody>
                        <div className="flex flex-wrap gap-4">
                          {client.customFields.map((field, index) => (
                            <div key={index} className="flex-1 min-w-[200px] max-w-full">
                              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 h-full">
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                  {field.label}
                                </h4>
                                <div className="text-sm text-gray-600">
                                  {field.type === 'boolean' ? (
                                    <span className={`font-medium ${field.value ? 'text-green-600' : 'text-red-600'}`}>
                                      {field.value ? t('common.yes') || 'Yes' : t('common.no') || 'No'}
                                    </span>
                                  ) : field.type === 'date' ? (
                                    <span className="font-medium">
                                      {field.value ? dayjs(field.value).format('MMM D, YYYY') : <span className="text-gray-400 italic">{t('common.notSet') || 'Not set'}</span>}
                                    </span>
                                  ) : field.type === 'number' ? (
                                    <span className="font-medium">
                                      {field.value !== null && field.value !== undefined
                                        ? (typeof field.value === 'number' ? field.value.toLocaleString() : field.value)
                                        : <span className="text-gray-400 italic">{t('common.notSet') || 'Not set'}</span>}
                                    </span>
                                  ) : field.type === 'textarea' ? (
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                      {field.value || <span className="text-gray-400 italic">{t('common.notSet') || 'Not set'}</span>}
                                    </p>
                                  ) : (
                                    <span className="font-medium">
                                      {field.value || <span className="text-gray-400 italic">{t('common.notSet') || 'Not set'}</span>}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {/* Notes */}
                  {client.notes && (
                    <Card className="lg:col-span-2">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <DocumentTextIcon className="w-5 h-5 text-primary" />
                          {t('common.notes')}
                        </CardTitle>
                      </CardHeader>
                      <CardBody>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{client.notes}</p>
                      </CardBody>
                    </Card>
                  )}
                </div>
              </TabPanel>

              {/* Projects Tab */}
              <TabPanel active={activeTab === 'projects'}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">{t('clients.projects')}</h3>
                  <Button size="sm" icon={PlusIcon} onClick={() => navigate('/projects/new', { state: { clientId: id } })}>
                    {t('projects.newProject')}
                  </Button>
                </div>

                {client.projects && client.projects.length > 0 ? (
                  <div className="space-y-3">
                    {client.projects.map((project) => (
                      <Link
                        key={project._id}
                        to={`/projects/${project._id}`}
                        className="block p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-lg mb-1">{project.name}</h4>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <StatusBadge status={project.status} />
                              {project.progress !== undefined && (
                                <span>Progress: {project.progress}%</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    title={t('clients.noProjects') || 'No projects yet'}
                    description={t('clients.noProjectsDesc') || 'Create a new project for this client'}
                    action={() => navigate('/projects/new', { state: { clientId: id } })}
                    actionLabel={t('projects.newProject')}
                  />
                )}
              </TabPanel>

              {/* Interactions Tab */}
              <TabPanel active={activeTab === 'interactions'}>
                <div className="flex justify-end mb-4">
                  <Button size="sm" icon={PlusIcon} onClick={() => {
                    setEditingInteraction(null);
                    resetInteraction();
                    setShowInteractionModal(true);
                  }}>
                    {t('clients.addInteraction')}
                  </Button>
                </div>

                {client.interactions && client.interactions.length > 0 ? (
                  <div className="space-y-4">
                    {client.interactions.slice().reverse().map((interaction) => (
                      <div key={interaction._id} className="p-4 bg-gray-50 rounded-lg border-l-4 border-primary">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="primary">{interaction.type}</Badge>
                            {interaction.subject && (
                              <span className="font-medium">{interaction.subject}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">
                              {dayjs(interaction.date).format('MMM D, YYYY h:mm A')}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={PencilIcon}
                              onClick={() => handleEditInteraction(interaction)}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={TrashIcon}
                              onClick={() => handleDeleteInteraction(interaction._id)}
                            />
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mt-2">{interaction.content}</p>
                        {interaction.duration && (
                          <p className="text-xs text-gray-500 mt-2">
                            {t('clients.duration') || 'Duration'}: {interaction.duration} {t('common.minutes') || 'minutes'}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    title={t('clients.noInteractions') || 'No interactions yet'}
                    description={t('clients.noInteractionsDesc') || 'Add your first interaction'}
                    action={() => {
                      setEditingInteraction(null);
                      resetInteraction();
                      setShowInteractionModal(true);
                    }}
                    actionLabel={t('clients.addInteraction')}
                  />
                )}
              </TabPanel>

              {/* Follow-ups Tab */}
              <TabPanel active={activeTab === 'followups'}>
                <div className="flex justify-end mb-4">
                  <Button size="sm" icon={PlusIcon} onClick={() => setShowFollowUpModal(true)}>
                    {t('clients.addFollowUp')}
                  </Button>
                </div>

                {client.followUps && client.followUps.length > 0 ? (
                  <div className="space-y-3">
                    {client.followUps
                      .sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate))
                      .map((followUp) => (
                        <div
                          key={followUp._id}
                          className={`p-4 rounded-lg border-l-4 ${followUp.isCompleted
                            ? 'bg-gray-50 border-gray-300'
                            : 'bg-accent/10 border-accent'
                            }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant={followUp.isCompleted ? 'neutral' : 'accent'}>
                                  {followUp.type}
                                </Badge>
                                {followUp.isCompleted && (
                                  <CheckCircleIcon className="w-5 h-5 text-green-500" />
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                                <CalendarIcon className="w-4 h-4" />
                                <span>{dayjs(followUp.scheduledDate).format('MMM D, YYYY h:mm A')}</span>
                              </div>
                              {followUp.notes && (
                                <p className="text-sm text-gray-700 mt-2">{followUp.notes}</p>
                              )}
                              {followUp.isCompleted && followUp.completedAt && (
                                <p className="text-xs text-gray-500 mt-2">
                                  {t('clients.completedAt') || 'Completed'}: {dayjs(followUp.completedAt).format('MMM D, YYYY h:mm A')}
                                </p>
                              )}
                            </div>
                            {!followUp.isCompleted && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCompleteFollowUp(followUp._id)}
                              >
                                {t('clients.complete') || 'Complete'}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <EmptyState
                    title={t('clients.noFollowUps') || 'No follow-ups scheduled'}
                    description={t('clients.noFollowUpsDesc') || 'Schedule a follow-up'}
                    action={() => setShowFollowUpModal(true)}
                    actionLabel={t('clients.addFollowUp')}
                  />
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>

      {/* Add/Edit Interaction Modal */}
      <Modal
        isOpen={showInteractionModal}
        onClose={() => {
          setShowInteractionModal(false);
          setEditingInteraction(null);
          resetInteraction();
        }}
        title={editingInteraction ? t('clients.editInteraction') || 'Edit Interaction' : t('clients.addInteraction')}
      >
        <form onSubmit={handleInteractionSubmit(onInteractionSubmit)}>
          <ModalBody>
            <div className="space-y-4">
              <Select
                label={t('clients.interactionType') || 'Type'}
                options={interactionTypes}
                {...registerInteraction('type', { required: true })}
              />
              <Input
                label={t('clients.subject') || 'Subject'}
                {...registerInteraction('subject')}
              />
              <Textarea
                label={t('clients.content') || 'Content'}
                rows={4}
                {...registerInteraction('content', { required: true })}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => setShowInteractionModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit">{t('common.add')}</Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Client Account Modal */}
      {client && !client.userAccount && (
        <Modal
          isOpen={showAccountModal}
          onClose={() => {
            setShowAccountModal(false);
            setAccountCreated(null);
          }}
          title={t('clients.createAccount') || 'Create Client Account'}
        >
          <ModalBody>
            {accountCreated ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-green-800">
                        {t('clients.accountCreatedSuccess') || 'Account created successfully!'}
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        {t('clients.emailSent') || 'A professional email with login credentials has been sent to the client.'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <p className="text-sm text-green-800 mb-2">
                      <strong>{t('clients.emailSentTo') || 'Email sent to'}:</strong> {accountCreated.email}
                    </p>
                    <p className="text-xs text-green-700">
                      {t('clients.emailContainsCredentials') || 'The email contains their login credentials and instructions on how to access their portal.'}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  {t('clients.createAccountDesc') || 'This will create a user account for the client with access to their projects. The client will receive login credentials.'}
                </p>
                {!client.email && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      {t('clients.emailRequired') || 'Client must have an email address to create an account.'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            {accountCreated ? (
              <Button onClick={() => {
                setShowAccountModal(false);
                setAccountCreated(null);
              }}>
                {t('common.close')}
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowAccountModal(false)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleCreateAccount} disabled={!client.email}>
                  {t('clients.createAccount') || 'Create Account'}
                </Button>
              </>
            )}
          </ModalFooter>
        </Modal>
      )}

      {/* Add Follow-up Modal */}
      <Modal
        isOpen={showFollowUpModal}
        onClose={() => setShowFollowUpModal(false)}
        title={t('clients.addFollowUp')}
      >
        <form onSubmit={handleFollowUpSubmit(onFollowUpSubmit)}>
          <ModalBody>
            <div className="space-y-4">
              <Controller
                name="type"
                control={controlFollowUp}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    label={t('clients.interactionType') || 'Type'}
                    options={interactionTypes}
                    {...field}
                  />
                )}
              />
              <Input
                label={t('clients.scheduledDate') || 'Scheduled Date'}
                type="datetime-local"
                {...registerFollowUp('scheduledDate', { required: true })}
              />
              <Textarea
                label={t('common.notes')}
                rows={3}
                {...registerFollowUp('notes')}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => setShowFollowUpModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit">{t('common.add')}</Button>
          </ModalFooter>
        </form>
      </Modal>
    </>
  );
}
